import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { exponential, quartic } from '@juliendargelos/easings';
import { BitmapText, Container, useTick } from '@pixi/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addSeatedPlayerChips, startWinChipsAnimation } from '../../../../redux/actions/pokerActions';
import { useAppDispatch } from '../../../../redux/store';
import { useReduxStore } from '../../../../redux/useReduxStore';
import { getChipsDisplayStr } from '../../../../utils/chipsDisplayUtils';
import { maybePlayAddChipsSound } from '../../../../utils/soundUtils';
import { getBitmapTextDimensions } from '../../../../utils/textMetricsUtils';
import { isTestEnvironment } from '../../../../utils/utils';
const PRE_MOVING_DURATION_MS = 500;
const MOVING_DURATION_MS = isTestEnvironment() ? 1000 : 2000;
const TOTAL_DURATION_MS = PRE_MOVING_DURATION_MS + MOVING_DURATION_MS;
const LETTER_SPACING = 1;
function getCurrentPositionFromStart(elapsedMs, pixelDelta, fn) {
    if (elapsedMs <= PRE_MOVING_DURATION_MS) {
        return 0;
    }
    const elapsedRatio = Math.min(1, (elapsedMs - PRE_MOVING_DURATION_MS) / MOVING_DURATION_MS);
    return fn(elapsedRatio) * pixelDelta;
}
export const MovingPotDisplay = ({ potIndex, potSplitIndex, x, y, amount, destinationSeatIndex, deltaX, deltaY, startTimestampMs, currency, showChipsAsBbMultiple, bigBlind }) => {
    const dispatch = useAppDispatch();
    const [isVisible, setIsVisible] = useState(true);
    const [isAnimating, setIsAnimating] = useState(true);
    const { getState } = useReduxStore();
    const containerRef = useRef(null);
    const text = useMemo(() => getChipsDisplayStr({ numChips: amount, showChipsAsBbMultiple, bigBlind }), [amount, bigBlind, showChipsAsBbMultiple]);
    const bitmapFontName = currency === 'chips' ? 'chips-pot-amount' : 'jacks-pot-amount';
    const dimensions = useMemo(() => getBitmapTextDimensions(text, bitmapFontName, { letterSpacing: LETTER_SPACING }), [text, bitmapFontName]);
    const startX = x + dimensions.w / 2;
    const startY = y - dimensions.h / 2;
    const animate = useCallback(() => {
        if (!containerRef.current) {
            return;
        }
        const nowMs = performance.now();
        const elapsedMs = nowMs - startTimestampMs;
        containerRef.current.x = startX + getCurrentPositionFromStart(elapsedMs, deltaX - dimensions.w / 2, quartic);
        containerRef.current.y = startY + getCurrentPositionFromStart(elapsedMs, deltaY + dimensions.h / 2, exponential);
        if (elapsedMs >= TOTAL_DURATION_MS) {
            // Wherever the chips are at this moment, time is up for getting to their destination.
            if (potSplitIndex === 0) {
                // In case of a split pot, only play the 'add chips' sound once when chips arrive at player.
                maybePlayAddChipsSound(getState());
            }
            setIsVisible(false);
            setIsAnimating(false);
            dispatch(addSeatedPlayerChips(destinationSeatIndex, amount));
            dispatch(startWinChipsAnimation(destinationSeatIndex));
        }
    }, [
        amount,
        deltaX,
        deltaY,
        destinationSeatIndex,
        dispatch,
        getState,
        potSplitIndex,
        startTimestampMs,
        startX,
        startY,
        dimensions.h,
        dimensions.w
    ]);
    useEffect(() => {
        setIsAnimating(true);
    }, [startTimestampMs]);
    useTick(animate, isAnimating);
    return (_jsx(Container, { ref: containerRef, x: startX, y: startY, children: isVisible && (_jsx(BitmapText, { "pixi-data": isTestEnvironment() ? { type: 'moving-pot-amount', potIndex, destinationSeatIndex } : null, style: { fontName: bitmapFontName, letterSpacing: LETTER_SPACING }, text: text, anchor: [1, 0] })) }));
};
